
import React from 'react';
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import { SettingsSectionProps } from '../types';
import { Card } from "@/components/ui/card";
import { motion } from "framer-motion";

const GameSpeedSettings = ({ localSettings, setLocalSettings }: SettingsSectionProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="p-5 border border-white/10 bg-gradient-to-br from-muted/50 to-background shadow-md hover:shadow-lg transition-all">
        <div className="space-y-5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <Label htmlFor="gameSpeed" className="text-lg font-medium text-foreground font-playfair">Game Speed</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <motion.div whileHover={{ rotate: 15 }} transition={{ duration: 0.2 }}>
                      <HelpCircle className="h-4 w-4 text-muted-foreground hover:text-primary transition-colors" />
                    </motion.div>
                  </TooltipTrigger>
                  <TooltipContent className="bg-card border-white/10 backdrop-blur-sm">
                    <p className="w-[200px] text-sm">Controls how many challenges appear per year</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <motion.div 
              className="font-semibold text-md relative"
              key={localSettings.gameSpeed}
              initial={{ scale: 1.2 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 400, damping: 10 }}
            >
              <span className="bg-gradient-to-r from-primary via-primary to-accent bg-clip-text text-transparent">
                {localSettings.gameSpeed}x
              </span>
              <div className="absolute inset-0 -z-10 blur-sm opacity-30 bg-primary rounded-full"></div>
            </motion.div>
          </div>
          
          <Slider 
            id="gameSpeed"
            min={0.5}
            max={2}
            step={0.5}
            value={[localSettings.gameSpeed]}
            className="h-2"
            onValueChange={(value) => 
              setLocalSettings({
                ...localSettings,
                gameSpeed: value[0]
              })
            }
          />
          
          <motion.p 
            className="text-sm text-muted-foreground/90 italic"
            key={localSettings.gameSpeed}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {localSettings.gameSpeed < 1 
              ? "Slower pace with fewer challenges" 
              : localSettings.gameSpeed > 1 
                ? "Faster pace with more frequent challenges"
                : "Standard game pace"}
          </motion.p>
        </div>
      </Card>
    </motion.div>
  );
};

export default GameSpeedSettings;
